import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  getMySchool,
  isSchoolProfile,
  updateMySchool as updateMySchoolApi,
} from '../api/v1/schools/schoolActions';
import {
  clearMySchoolCache,
  MY_SCHOOL_UPDATED_EVENT,
  setMySchoolCache,
} from '../lib/mySchoolCache';

const MySchoolContext = createContext(null);

const ROLES_WITHOUT_SCHOOL_PROFILE = new Set(['admin', 'guest', 'student', 'parent']);

export function MySchoolProvider({ children }) {
  const { accessToken, isAuthenticated, user } = useAuth();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const schoolRef = useRef(null);

  useEffect(() => {
    schoolRef.current = school;
  }, [school]);

  const applySchool = useCallback((data) => {
    const next = isSchoolProfile(data) ? data : null;
    setSchool(next);
    if (accessToken) setMySchoolCache(accessToken, next);
    return next;
  }, [accessToken]);

  const loadSchool = useCallback(
    async ({ refresh = false } = {}) => {
      if (!isAuthenticated || !accessToken) {
        clearMySchoolCache();
        setSchool(null);
        setLoading(false);
        return null;
      }

      const role = user?.role?.toLowerCase();
      if (role && ROLES_WITHOUT_SCHOOL_PROFILE.has(role)) {
        clearMySchoolCache();
        setSchool(null);
        setLoading(false);
        return null;
      }

      const isInitialLoad = schoolRef.current === null && !refresh;
      if (isInitialLoad) {
        setLoading(true);
      }

      try {
        const data = await getMySchool(accessToken, { refresh, role });
        return applySchool(data);
      } catch {
        applySchool(null);
        return null;
      } finally {
        if (isInitialLoad) {
          setLoading(false);
        }
      }
    },
    [accessToken, isAuthenticated, applySchool, user?.role],
  );

  useEffect(() => {
    loadSchool();
  }, [loadSchool]);

  useEffect(() => {
    const onTokenChange = (e) => {
      if (!e.detail) {
        clearMySchoolCache();
        setSchool(null);
        setLoading(false);
      }
    };
    window.addEventListener('token_changed', onTokenChange);
    return () => window.removeEventListener('token_changed', onTokenChange);
  }, []);

  useEffect(() => {
    const onUpdated = (e) => {
      applySchool(e.detail);
    };
    window.addEventListener(MY_SCHOOL_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(MY_SCHOOL_UPDATED_EVENT, onUpdated);
  }, [applySchool]);

  const refreshSchool = useCallback(() => loadSchool({ refresh: true }), [loadSchool]);

  const updateMySchool = useCallback(
    async (schoolData) => {
      if (!accessToken) return null;
      const updated = await updateMySchoolApi(schoolData, accessToken);
      return applySchool(updated);
    },
    [accessToken, applySchool],
  );

  const value = useMemo(
    () => ({
      school,
      schoolId: school?.id ?? null,
      hasProfile: isSchoolProfile(school),
      loading,
      refreshSchool,
      updateMySchool,
      setSchool: applySchool,
    }),
    [school, loading, refreshSchool, updateMySchool, applySchool],
  );

  return <MySchoolContext.Provider value={value}>{children}</MySchoolContext.Provider>;
}

export function useMySchool() {
  const ctx = useContext(MySchoolContext);
  if (!ctx) {
    throw new Error('useMySchool must be used within MySchoolProvider');
  }
  return ctx;
}
